/**
 * Theme Registry — custom named themes with visibility control.
 *
 * **Storage strategy:** API-first with localStorage cache.
 *   - On app init, `hydrateThemesFromApi()` fetches from the server
 *     and writes to localStorage for fast subsequent reads.
 *   - All reads come from localStorage (synchronous, fast).
 *   - All writes go to the API first, then update localStorage.
 *   - Cross-tab sync via the browser `storage` event still works.
 *
 * Built-in themes ('dark', 'light') are always available but can have
 * their visibility toggled.
 */

import {
  fetchCustomThemes,
  createCustomTheme as apiCreate,
  updateCustomTheme as apiUpdate,
  deleteCustomThemeApi,
  fetchThemeOverrides,
  saveThemeOverrideApi,
} from '../api/customThemes';
import type { CustomThemeDto, ThemeOverrideDto } from '../api/customThemes';
import { registerThemeLookup } from './themeOverrides';

const LS_KEY = 'l4e_custom_themes';
const VIS_KEY = 'l4e_theme_visibility';
const META_KEY = 'l4e_theme_metadata';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface CustomTheme {
  /** Display name, e.g. "Ocean Breeze" */
  name: string;
  /** Which built-in theme this derives from */
  baseTheme: 'dark' | 'light';
  /** Token overrides (only tokens that differ from the base) */
  tokenOverrides: Record<string, string>;
  /** Created timestamp */
  createdAt: string;
  /** Last modified timestamp */
  updatedAt: string;
}

export interface ThemeVisibility {
  /** Map of theme name → visible boolean. Missing = default visible. */
  [themeName: string]: boolean;
}

/* ------------------------------------------------------------------ */
/*  API → localStorage hydration                                       */
/* ------------------------------------------------------------------ */

/** Whether the initial hydration from the API has completed. */
let _hydrated = false;

/**
 * Fetch all custom themes and overrides from the API and
 * write them to localStorage. Call once on app startup.
 * Returns true if hydration succeeded, false if it fell back to cache.
 */
export async function hydrateThemesFromApi(): Promise<boolean> {
  try {
    const [themes, overrides] = await Promise.all([
      fetchCustomThemes(),
      fetchThemeOverrides(),
    ]);

    // Build localStorage structures from API data
    const customMap: Record<string, CustomTheme> = {};
    const visMap: ThemeVisibility = {};
    const metaMap: Record<string, ThemeMetaEntry> = {};

    for (const t of themes) {
      customMap[t.name] = {
        name: t.name,
        baseTheme: t.baseTheme,
        tokenOverrides: t.tokenOverrides || {},
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
      };
      visMap[t.name] = t.isVisible;
      if (t.displayName || t.icon) {
        metaMap[t.name] = {};
        if (t.displayName) metaMap[t.name].displayName = t.displayName;
        if (t.icon) metaMap[t.name].icon = t.icon;
      }
    }

    // Write to localStorage (this also triggers cross-tab sync via storage event)
    window?.localStorage?.setItem(LS_KEY, JSON.stringify(customMap));
    window?.localStorage?.setItem(VIS_KEY, JSON.stringify(visMap));
    window?.localStorage?.setItem(META_KEY, JSON.stringify(metaMap));

    // Also hydrate the l4e_theme_overrides key from the overrides table
    const overrideMap: Record<string, { highlightColor?: string; tokenOverrides?: Record<string, string> }> = {};
    for (const o of overrides) {
      const entry: { highlightColor?: string; tokenOverrides?: Record<string, string> } = {};
      if (o.highlightColor) entry.highlightColor = o.highlightColor;
      if (o.tokenOverrides && Object.keys(o.tokenOverrides).length > 0) {
        entry.tokenOverrides = o.tokenOverrides;
      }
      if (Object.keys(entry).length > 0) {
        overrideMap[o.themeName] = entry;
      }
    }
    window?.localStorage?.setItem('l4e_theme_overrides', JSON.stringify(overrideMap));

    _hydrated = true;
    // Notify same-tab listeners to re-resolve
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    return true;
  } catch (err) {
    // API unavailable — fall back to whatever is in localStorage
    console.warn('[ThemeRegistry] API hydration failed, using localStorage cache:', err);
    _hydrated = false;
    return false;
  }
}

/** Whether themes have been hydrated from the API. */
export function isThemeHydrated(): boolean {
  return _hydrated;
}

/* ------------------------------------------------------------------ */
/*  Custom theme CRUD (API-backed)                                     */
/* ------------------------------------------------------------------ */

/** Read all custom themes from localStorage cache. */
export function readCustomThemes(): Record<string, CustomTheme> {
  try {
    const raw = window?.localStorage?.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as Record<string, CustomTheme>;
  } catch { /* ignore */ }
  return {};
}

/** Get a single custom theme by name from cache. */
export function getCustomTheme(name: string): CustomTheme | null {
  return readCustomThemes()[name] || null;
}

// Register this lookup with themeOverrides to break circular dependency.
registerThemeLookup(getCustomTheme);

/**
 * Save (create or update) a custom theme.
 * Updates localStorage immediately (synchronous), then persists to API in background.
 * This keeps the UI responsive — the API call is fire-and-forget.
 */
export function saveCustomTheme(theme: CustomTheme): void {
  // 1. Update localStorage cache immediately
  const all = readCustomThemes();
  const isNew = !(theme.name in all);
  all[theme.name] = { ...theme, updatedAt: new Date().toISOString() };
  window?.localStorage?.setItem(LS_KEY, JSON.stringify(all));

  // 2. Fire-and-forget API persistence
  const apiCall = isNew
    ? apiCreate({
        name: theme.name,
        baseTheme: theme.baseTheme,
        tokenOverrides: theme.tokenOverrides,
        isVisible: true,
      })
    : apiUpdate(theme.name, {
        baseTheme: theme.baseTheme,
        tokenOverrides: theme.tokenOverrides,
      });
  apiCall.catch((err) => {
    console.warn('[ThemeRegistry] API save failed (localStorage updated):', err);
  });
}

/**
 * Delete a custom theme.
 * Removes from localStorage immediately, then from API in background.
 */
export function deleteCustomTheme(name: string): void {
  // 1. Remove from localStorage cache
  const all = readCustomThemes();
  delete all[name];
  window?.localStorage?.setItem(LS_KEY, JSON.stringify(all));

  const vis = readThemeVisibility();
  delete vis[name];
  window?.localStorage?.setItem(VIS_KEY, JSON.stringify(vis));

  // 2. Fire-and-forget API delete
  deleteCustomThemeApi(name).catch((err) => {
    console.warn('[ThemeRegistry] API delete failed (localStorage updated):', err);
  });
}

/**
 * Rename a custom theme.
 * Updates localStorage immediately, then API in background.
 */
export function renameCustomTheme(oldName: string, newName: string): void {
  // 1. Update localStorage cache
  const all = readCustomThemes();
  const theme = all[oldName];
  if (!theme) return;
  delete all[oldName];
  theme.name = newName;
  theme.updatedAt = new Date().toISOString();
  all[newName] = theme;
  window?.localStorage?.setItem(LS_KEY, JSON.stringify(all));

  // Transfer visibility
  const vis = readThemeVisibility();
  if (oldName in vis) {
    vis[newName] = vis[oldName];
    delete vis[oldName];
    window?.localStorage?.setItem(VIS_KEY, JSON.stringify(vis));
  }

  // 2. Fire-and-forget API rename
  apiUpdate(oldName, { newName }).catch((err) => {
    console.warn('[ThemeRegistry] API rename failed (localStorage updated):', err);
  });
}

/** List all custom theme names. */
export function listCustomThemeNames(): string[] {
  return Object.keys(readCustomThemes());
}

/* ------------------------------------------------------------------ */
/*  Visibility                                                         */
/* ------------------------------------------------------------------ */

/** Read visibility map. */
export function readThemeVisibility(): ThemeVisibility {
  try {
    const raw = window?.localStorage?.getItem(VIS_KEY);
    if (raw) return JSON.parse(raw) as ThemeVisibility;
  } catch { /* ignore */ }
  return {};
}

/** Check if a theme is visible in the user profile selector. */
export function isThemeVisible(name: string): boolean {
  const vis = readThemeVisibility();
  if (name in vis) return vis[name];
  return true;
}

/**
 * Set visibility for a theme.
 * Updates localStorage immediately, then API in background.
 */
export function setThemeVisibility(name: string, visible: boolean): void {
  // 1. Update cache
  const vis = readThemeVisibility();
  vis[name] = visible;
  window?.localStorage?.setItem(VIS_KEY, JSON.stringify(vis));

  // 2. Fire-and-forget API update
  const existing = getCustomTheme(name);
  if (existing) {
    apiUpdate(name, { isVisible: visible }).catch((err) => {
      console.warn('[ThemeRegistry] API visibility update failed:', err);
    });
  }
}

/** Get all themes that should appear in the user profile selector. */
export function getVisibleThemes(): Array<{
  name: string; isCustom: boolean; baseTheme?: 'dark' | 'light';
  displayName?: string; icon?: string;
}> {
  const result: Array<{
    name: string; isCustom: boolean; baseTheme?: 'dark' | 'light';
    displayName?: string; icon?: string;
  }> = [];
  const meta = readThemeMetadata();

  // Built-in themes
  if (isThemeVisible('dark')) {
    const m = meta['dark'];
    result.push({ name: 'dark', isCustom: false, displayName: m?.displayName, icon: m?.icon });
  }
  if (isThemeVisible('light')) {
    const m = meta['light'];
    result.push({ name: 'light', isCustom: false, displayName: m?.displayName, icon: m?.icon });
  }

  // Custom themes
  const customs = readCustomThemes();
  for (const [name, theme] of Object.entries(customs)) {
    if (isThemeVisible(name)) {
      const m = meta[name];
      result.push({
        name, isCustom: true, baseTheme: theme.baseTheme,
        displayName: m?.displayName, icon: m?.icon,
      });
    }
  }

  return result;
}

/* ------------------------------------------------------------------ */
/*  Theme Metadata (display name, icon)                                */
/* ------------------------------------------------------------------ */

export interface ThemeMetaEntry {
  displayName?: string;
  icon?: string;
}

/** Read all theme metadata. */
export function readThemeMetadata(): Record<string, ThemeMetaEntry> {
  try {
    const raw = window?.localStorage?.getItem(META_KEY);
    if (raw) return JSON.parse(raw) as Record<string, ThemeMetaEntry>;
  } catch { /* ignore */ }
  return {};
}

/** Get metadata for a specific theme. */
export function getThemeMetadata(name: string): ThemeMetaEntry | null {
  return readThemeMetadata()[name] || null;
}

/**
 * Set metadata for a theme (displayName, icon).
 * Updates localStorage immediately, then API in background.
 */
export function setThemeMetadata(name: string, meta: ThemeMetaEntry): void {
  // 1. Update cache
  const all = readThemeMetadata();
  all[name] = { ...(all[name] || {}), ...meta };
  if (!all[name].displayName) delete all[name].displayName;
  if (!all[name].icon) delete all[name].icon;
  if (Object.keys(all[name]).length === 0) delete all[name];
  window?.localStorage?.setItem(META_KEY, JSON.stringify(all));

  // 2. Fire-and-forget API update
  const existing = getCustomTheme(name);
  if (existing) {
    apiUpdate(name, {
      displayName: meta.displayName,
      icon: meta.icon,
    }).catch((err) => {
      console.warn('[ThemeRegistry] API metadata update failed:', err);
    });
  }
}
